/**
 * useLiveTestingAnswers Hook
 *
 * Live Testing Preview에서 사용하는 로컬 답변 상태 관리
 * 서버에 저장하지 않고 미리보기 용도로만 사용
 */

import { useState, useCallback, useEffect } from 'react';
import { LiveTestingAnswerMap, LiveTestingAnswerValue } from './types';

interface UseLiveTestingAnswersProps {
  /** Tree가 변경되면 답변 초기화를 위한 키 */
  treeKey?: string;
  /** Tree 변경 시 답변 초기화 여부 (기본값: true) */
  resetOnTreeChange?: boolean;
}

interface UseLiveTestingAnswersReturn {
  /** 모든 질문에 대한 답변 맵 (questionKey → 답변) */
  answerMap: LiveTestingAnswerMap;
  /** 특정 질문의 답변 업데이트 */
  updateAnswer: (questionKey: string, value: LiveTestingAnswerValue) => void;
  /** 모든 답변 초기화 */
  resetAnswers: () => void;
  /** 특정 질문의 답변 가져오기 */
  getAnswer: (questionKey: string) => LiveTestingAnswerValue;
  /** 특정 질문에 답변이 있는지 확인 */
  hasAnswer: (questionKey: string) => boolean;
}

/**
 * Live Testing용 로컬 답변 상태 관리 훅
 *
 * @example
 * ```tsx
 * const { answerMap, updateAnswer, resetAnswers } = useLiveTestingAnswers({
 *   treeKey: selectedTreeKey,
 * });
 *
 * // 답변 업데이트
 * updateAnswer('patient-name', 'John Doe');
 *
 * // Select 타입 답변 업데이트
 * updateAnswer('pain-level', { selectedKeys: ['severe'], notesForKeys: {} });
 * ```
 */
export function useLiveTestingAnswers({
  treeKey,
  resetOnTreeChange = true,
}: UseLiveTestingAnswersProps = {}): UseLiveTestingAnswersReturn {
  const [answerMap, setAnswerMap] = useState<LiveTestingAnswerMap>({});

  // Tree가 변경되면 답변 초기화
  useEffect(() => {
    if (resetOnTreeChange) {
      setAnswerMap({});
    }
  }, [resetOnTreeChange, treeKey]);

  // 특정 질문의 답변 업데이트
  const updateAnswer = useCallback(
    (questionKey: string, value: LiveTestingAnswerValue) => {
      setAnswerMap((prev) => ({
        ...prev,
        [questionKey]: value,
      }));
    },
    []
  );

  // 모든 답변 초기화
  const resetAnswers = useCallback(() => {
    setAnswerMap({});
  }, []);

  // 특정 질문의 답변 가져오기
  const getAnswer = useCallback(
    (questionKey: string): LiveTestingAnswerValue => {
      return answerMap[questionKey];
    },
    [answerMap]
  );

  // 특정 질문에 답변이 있는지 확인
  const hasAnswer = useCallback(
    (questionKey: string): boolean => {
      const value = answerMap[questionKey];
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.length > 0;
      if (typeof value === 'number') return true;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object') {
        // SelectValue 체크
        if ('selectedKeys' in value) {
          return (value.selectedKeys as string[]).length > 0;
        }
        // 다른 객체 타입은 키가 있으면 true
        return Object.keys(value).length > 0;
      }
      return false;
    },
    [answerMap]
  );

  return {
    answerMap,
    updateAnswer,
    resetAnswers,
    getAnswer,
    hasAnswer,
  };
}
